// this file is different from the other ConditionalPlugin directory

import { addClassNamesToElement } from "@lexical/utils";
import { isContractValidationPage, isSetupPage } from "helpers/utils";
import type {
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    Spread,
} from "lexical";
import { DOMConversionMap, ElementNode } from "lexical";

type SerializedConditionalNode = Spread<
    {
        show: boolean;
    },
    SerializedElementNode
>;

export class ConditionalNode extends ElementNode {
    __show: boolean = true;

    getShow(): boolean {
        const self = this.getLatest();
        return self.__show;
    }

    setShow(value: boolean) {
        const self = this.getWritable();
        self.__show = value;
    }

    toggleShow(): void {
        this.setShow(!this.getShow());
    }

    constructor(show: boolean, key?: NodeKey) {
        super(key);
        this.__show = show;
    }

    static getType(): string {
        return "conditional";
    }

    static clone(node: ConditionalNode): ConditionalNode {
        return new ConditionalNode(node.__show, node.__key);
    }

    createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
        const dom = document.createElement("p");
        const theme = config.theme;
        const className = theme.conditional;
        if (className !== undefined && !isContractValidationPage()) {
            addClassNamesToElement(dom, className);
        }
        dom.classList.toggle("show", this.__show);
        if (isSetupPage()) {
            const checkbox = dom.appendChild(document.createElement("input"));
            checkbox.type = "checkbox";
            checkbox.checked = this.getShow();
            addClassNamesToElement(checkbox, theme.conditionalCheckbox);
            checkbox.addEventListener("change", (e) => {
                const show = editor.getEditorState().read(() => this.getShow());
                if (show !== checkbox.checked) {
                    editor.update(() => this.toggleShow());
                }
            });
        }
        return dom;
    }

    updateDOM(prevNode: ConditionalNode, dom: HTMLDetailsElement): boolean {
        if (prevNode.__show !== this.__show) {
            (dom.firstChild as HTMLInputElement).checked = this.__show;
            dom.classList.toggle("show", this.__show);
        }

        return false;
    }

    static importJSON(serializedNode: SerializedConditionalNode): ConditionalNode {
        const node = $createConditionalNode(serializedNode.show);
        node.setFormat(serializedNode.format);
        node.setIndent(serializedNode.indent);
        node.setDirection(serializedNode.direction);
        return node;
    }

    exportJSON(): SerializedConditionalNode {
        return {
            ...super.exportJSON(),
            show: this.__show,
            type: "conditional",
            version: 1,
        };
    }

    exportDOM(editor: LexicalEditor): DOMExportOutput {
        const { element } = super.exportDOM(editor);
        if (!this.__show) {
            return { element: null };
        }
        return { element };
    }
}

export function $createConditionalNode(show: boolean): ConditionalNode {
    return new ConditionalNode(show);
}

export function $isConditionalNode(node: LexicalNode | null | undefined): node is ConditionalNode {
    return node instanceof ConditionalNode;
}
